import React, {useEffect, useState, useCallback, useMemo, useRef} from 'react';
import {useLocation} from 'react-router-dom';
import StandardReports from '../../StandardReports';
import {
  chunkRows,
  createTableColumns,
  extractFilterFieldsForReviewResults,
  mapRowsWithHeaders,
  mergeApiDataIntoConfigForReviewResult,
  reportApiMap,
  resolveSelectedSource,
} from '../../common/ReportHelper';
import {Reports, RightPanelConfig} from '../../config/reportTypes';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../../redux/store';
import {SourceItem} from '../../../tally/selectSourceContainer/ArrSourceSelectedContainer';
import dayjs from 'dayjs';
import ArrLoadingSpinner from '../../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {
  PAGE_SIZE,
  REPORT_PAGE_SIZE_OPTIONS,
  ToastType,
} from '../../../../../shared/constants';
import {
  useExportReviewResults,
  useLeftPanelReport,
  useReviewResultsViewDataQuery,
  useSaveReviewResultsReportData,
} from '../../../../../hooks/reporting/useReporting';
import {
  ReviewResultsReport,
  SaveReviewResultsReportDataPayload,
  SaveReviewResultsReportDataResponse,
} from '../../../../../api/types/reportingApi.type';
import ArrToastNotification from '../../../../common/arr-toast-notification/ArrToastNotification';
import {GridSortModel} from '@mui/x-data-grid-pro';
import ArrWaitToCompleteModalWrapper from '../../component/waittocomplete-modal-wrapper/ArrWaitToCompleteModalWrapper';
import DocCountSummaryView from './component/DocCountSummaryView/DocCountSummaryView';
import {t} from 'i18next';
import {isNil} from 'lodash';
import './ReviewResultContainer.scss';
interface ReportConfig {
  infoContent: string | string[] | undefined;
  title: string;
  showInfoIcon?: boolean;
  config: Reports;
}

interface PageSortConfigType {
  pageSize: number;
  pageNumber: number;
  sort: GridSortModel;
}
const DEFAULT_PAGE_SIZE = 50;

const ReviewResultsContainer: React.FC = () => {
  const [reportConfig, setReportConfig] = useState<ReportConfig | null>(null);
  const [dynamicData, setDynamicData] = useState<any>(null);
  const [resetCounter, setResetCounter] = useState(0);
  const [gridData, setGridData] = useState<any>(null);
  const [filterValues, setFilterValues] =
    useState<SaveReviewResultsReportDataPayload>({
      reportTypeCode: 'RRR008',
    } as SaveReviewResultsReportDataPayload);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastStatus, setToastStatus] = useState<ToastType[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(PAGE_SIZE);
  const location = useLocation();
  const {item, fromReportLanding = false} = location.state ?? {};
  const reportKey = location.pathname.split('/')[2];
  const viewKey = location.pathname.split('/')[3];
  const hasFetchedRef = useRef(false);
  const hasAutoFetchedViewReportRef = useRef(false);
  const userRole = useSelector(
    (state: RootState) => state?.authenticatedUser?.user?.roleName
  );
  const [saveApiCallResponse, setSaveApiCallResponse] =
    useState<SaveReviewResultsReportDataResponse>({
      notificationID: 0,
      reportID: 0,
    });
  const [paginationSortConfig, setPaginationConfig] =
    useState<PageSortConfigType>({
      pageSize: DEFAULT_PAGE_SIZE,
      pageNumber: 1,
      sort: [{field: 'userEmail', sort: 'asc'}],
    });

  const [selectedColumns, setSelectedColumns] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem('VisibleColumns') || '[]');
    } catch {
      return [];
    }
  });

  const paginationSortConfigRef =
    useRef<PageSortConfigType>(paginationSortConfig);

  const {mutateAsync: saveReviewResultsReportData} =
    useSaveReviewResultsReportData();
  const {mutateAsync: getReviewResults} = useReviewResultsViewDataQuery();
  const triggerToast = (message: string, status: ToastType[]) => {
    setToastMessage(message);
    setToastStatus(status);
    setShowToast(true);
  };

  const {mutate: exportReviewResultExcel} = useExportReviewResults();

  const isReviewResultsViewRoute = useMemo(() => {
    return reportKey === 'review-results' && viewKey === 'view';
  }, [reportKey, viewKey]);

  const reportIdFromUrl = useMemo(() => {
    if (!isReviewResultsViewRoute) return 0;
    const params = new URLSearchParams(location.search);
    const id = params.get('ReportID');
    const parsed = Number(id);
    return Number.isFinite(parsed) ? parsed : 0;
  }, [location.search, isReviewResultsViewRoute]);

  const resolvedReportKey = useMemo(() => {
    if (reportKey === 'review-results') return 'ReviewResults';
    return reportKey;
  }, [reportKey]);

  const leftPanelReportId = useMemo(() => {
    return isReviewResultsViewRoute && reportIdFromUrl > 0
      ? reportIdFromUrl
      : (undefined as any);
  }, [isReviewResultsViewRoute, reportIdFromUrl]);

  const {data: leftPanelReportData} = useLeftPanelReport(leftPanelReportId);
  const leftPanelResolvedReport = useMemo(() => {
    return leftPanelReportData ?? null;
  }, [leftPanelReportData]);
  // dataAsOf should only update when handleActionClick is triggered.
  // Initialize once (lazy) and then only update via setDataAsOf inside handleActionClick.
  const [dataAsOf, setDataAsOf] = useState<string>(() =>
    leftPanelResolvedReport?.dataAsOf
      ? dayjs
          .utc(leftPanelResolvedReport.dataAsOf)
          .format('YYYY/MM/DD HH:mm:ss')
      : gridData?.dataAsOf
        ? dayjs.utc(gridData?.dataAsOf).format('YYYY/MM/DD HH:mm:ss')
        : dayjs.utc().format('YYYY/MM/DD HH:mm:ss')
  );
  const toStringArray = (val: any[]) =>
    Array.isArray(val) ? val.map(String) : [];

  useEffect(() => {
    if (!leftPanelResolvedReport) return;
    if (!isReviewResultsViewRoute) return;
    if (resetCounter > 0) return;

    const updatedFilters: SaveReviewResultsReportDataPayload = {
      selected_ActionTypeID: toStringArray(
        leftPanelResolvedReport.selected_ActionTypeID
      ),
      selected_SearchID: toStringArray(
        leftPanelResolvedReport.selected_SearchID
      ),
      selectedDocumentTypes: toStringArray(
        leftPanelResolvedReport.selectedDocumentTypes
      ),
      selectedTagClassification: toStringArray(
        leftPanelResolvedReport.selectedTagClassification
      ),
      selectedTags: toStringArray(leftPanelResolvedReport.selectedTags),
      selectedRedactionTags: toStringArray(
        leftPanelResolvedReport.selectedRedactionTags
      ),
      selectedCommentTypes: toStringArray(
        leftPanelResolvedReport.selectedCommentTypes
      ),
      selectedProductions: toStringArray(
        leftPanelResolvedReport.selectedProductions
      ),
      selectedSourceID: toStringArray(leftPanelResolvedReport.selectedSourceID),

      selectedSource: leftPanelResolvedReport.selectedSource ?? '',
      fromDateTime: leftPanelResolvedReport.fromDateTime ?? '',
      toDateTime: leftPanelResolvedReport.toDateTime ?? '',
      selectedCustomColumns:
        leftPanelResolvedReport.selectedCustomColumns ?? [],

      reportTypeCode: 'RRR008',
      timeOffset: 0,
      sortingColumn: 'DocID',
      isAscending: 'asc',
      dataAsOf: leftPanelResolvedReport.dataAsOf,
    };

    setFilterValues(updatedFilters);
  }, [leftPanelResolvedReport, isReviewResultsViewRoute, resetCounter]);

  const didFiltersChange = useMemo(() => {
    if (!leftPanelResolvedReport) return true;

    const leftFilters = extractFilterFieldsForReviewResults(
      leftPanelResolvedReport
    );
    const currentFilters = extractFilterFieldsForReviewResults(filterValues);

    return JSON.stringify(leftFilters) !== JSON.stringify(currentFilters);
  }, [leftPanelResolvedReport, filterValues]);

  const handlePageNumber = useCallback((pageNumber: number) => {
    setPageNumber(pageNumber);
  }, []);

  const handleItemsPerPage = useCallback((itemsPerPage: number) => {
    setItemsPerPage(itemsPerPage);
    setPageNumber(1);
  }, []);

  useEffect(() => {
    if (!reportIdFromUrl) return;
    hasFetchedRef.current = false;
  }, [reportIdFromUrl, location.key]);

  const loadReportConfig = useCallback(async () => {
    const keyToUse = isReviewResultsViewRoute ? resolvedReportKey : reportKey;
    if (!keyToUse) return;
    try {
      const data = await import(`../../config/${keyToUse}.json`);
      setReportConfig(data.report);
    } catch (error) {
      console.error('Error loading JSON:', error);
    }
  }, [reportKey]);

  const loadDynamicData = useCallback(async () => {
    const apiFn = reportApiMap[resolvedReportKey];
    if (apiFn) {
      try {
        const data = await apiFn();
        setDynamicData(data);
      } catch (error: any) {
        console.error('Error fetching API data:', error);
        triggerToast(
          error?.response?.data?.title ?? 'Error fetching API data:',
          [ToastType.Error]
        );
      }
    }
  }, [reportKey]);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    const fetchData = async () => {
      setLoading(true);
      localStorage.removeItem('VisibleColumns');
      setSelectedColumns([]);
      await Promise.all([loadReportConfig(), loadDynamicData()]);
      setLoading(false);
    };
    fetchData();
  }, [loadReportConfig, loadDynamicData]);

  const sources: SourceItem[] = useMemo(() => {
    if (!dynamicData) return [];
    const src: any = Array.isArray(dynamicData) ? dynamicData[0] : dynamicData;
    const baseSources: SourceItem[] = [
      {
        id: 'SECURITYGROUPS',
        label: 'Security Groups',
        data: src?.securityGroupList ?? [],
      },
      {id: 'REVIEWERS', label: 'Reviewers', data: src?.reviewersList ?? []},
      {id: 'CUSTODIANS', label: 'Custodians', data: src?.custodiansList ?? []},
      {id: 'SEARCHES', label: 'Searches', data: src?.searchesList ?? []},
      {
        id: 'ASSIGNMENTS',
        label: 'Assignments',
        data: src?.assignmentsList ?? [],
      },
      {id: 'FOLDERS', label: 'Folders', data: src?.foldersList ?? []},
    ];
    return baseSources;
  }, [dynamicData, userRole]);

  const mergedConfig = useMemo(() => {
    if (!reportConfig || !dynamicData) return null;

    const initialValuesSource =
      resetCounter > 0 ? undefined : leftPanelResolvedReport;

    return mergeApiDataIntoConfigForReviewResult(
      reportConfig.config,
      dynamicData,
      initialValuesSource
    );
  }, [reportConfig, dynamicData, leftPanelResolvedReport, resetCounter]);

  const buildReportPayload = (
    ui: any,
    api: any
  ): SaveReviewResultsReportDataPayload => {
    const ids = resolveSelectedSource(ui)?.selectedSourceID ?? [];
    const selectedSourceresult =
      resolveSelectedSource(ui)?.selectedSource ?? '';
    let selectedSourceIDresult = [];

    if (selectedSourceresult === 'FOLDERS') {
      selectedSourceIDresult = Array.isArray(dynamicData?.foldersList)
        ? dynamicData.foldersList
            .filter((folder: any) => ids.includes(folder.id) && !folder.isGroup)
            .map((folder: any) => folder.id)
        : [];
    }

    if (selectedSourceresult === 'ASSIGNMENTS') {
      selectedSourceIDresult = Array.isArray(dynamicData?.assignmentsList)
        ? dynamicData.assignmentsList
            .filter((item: any) => ids.includes(item.id))
            .map((item: any) => item.id)
        : [];
    }

    if (selectedSourceresult === 'SEARCHES') {
      selectedSourceIDresult = Array.isArray(dynamicData?.searchesList)
        ? dynamicData.searchesList
            .filter((item: any) => ids.includes(item.id))
            .map((item: any) => item.id)
        : [];
    }

    if (selectedSourceresult === 'CUSTODIANS') {
      selectedSourceIDresult = Array.isArray(ids) ? ids : [];
    }

    if (selectedSourceresult === 'REVIEWERS') {
      selectedSourceIDresult = Array.isArray(ids) ? ids : [];
    }

    if (selectedSourceresult === 'SECURITYGROUPS') {
      selectedSourceIDresult = Array.isArray(ids) ? ids : [];
    }
    return {
      selected_ActionTypeID: api.actionTypesList
        .filter((_: any, idx: number) => ui.TagActionTypes?.[idx] === true)
        .map((a: any) => a.actionTypeID),
      selected_SearchID: [],
      selectedDocumentTypes: api.documentTypesList
        .filter((_: any, idx: number) => ui.documentTypes?.[idx] === true)
        .map((d: any) => String(d.keyID)),
      selectedTagClassification: api.tagClassifications
        .filter((_: any, idx: number) => ui.tagClassification?.[idx] === true)
        .map((x: any) => String(x.keyID)),
      selectedTags: ui.tagsTree ?? [],
      selectedRedactionTags: ui.redactionTree ?? [],
      selectedCommentTypes: api.commentsTypeList
        .filter((_: any, idx: number) => ui.commentTypes?.[idx] === true)
        .map((d: any) => String(d.keyID)),
      selectedProductions: (ui.productionTree ?? []).filter(
        (id: string) => !id.endsWith('g')
      ),
      reportTypeCode: 'RRR008',
      timeOffset: 0,
      sortingColumn: 'DocID',
      isAscending: 'asc',
      selectedSourceID: selectedSourceIDresult ?? [],
      selectedSource: resolveSelectedSource(ui)?.selectedSource ?? '',
      fromDateTime: Array.isArray(ui.fromDate)
        ? ui.fromDate.length
          ? ui.fromDate
          : ''
        : (ui.fromDate ?? ''),
      toDateTime: Array.isArray(ui.toDate)
        ? ui.toDate.length
          ? ui.toDate
          : ''
        : (ui.toDate ?? ''),

      selectedCustomColumns: selectedColumnIndexes,
    };
  };
  const fetchReportGridDataWithPagination = useCallback(
    async (
      reportId: number,
      sortModel: GridSortModel,
      paginationConfig: {pageNumber: number; pageSize: number}
    ) => {
      if (!reportId || reportId <= 0) return;

      paginationSortConfigRef.current = {
        pageSize: paginationConfig.pageSize,
        pageNumber: paginationConfig.pageNumber,
        sort: sortModel,
      };
      setPaginationConfig(paginationSortConfigRef.current);
      try {
        const payload = {
          reportID: saveApiCallResponse.reportID,
        };
        const gridResponse: ReviewResultsReport =
          await getReviewResults(payload);

        const rowData = gridResponse?.reviewDetails?.dataTableHeader.length;
        if (!Array.isArray(rowData) || rowData.length === 0) {
          gridResponse.noDataMessage = t(
            'reports.reviewResults.rightPanel.noResultPostApply'
          );
        }
        setGridData(gridResponse);
      } catch (error: any) {
        triggerToast(error?.response?.data?.title ?? 'Something went wrong', [
          ToastType.Error,
        ]);
      }
    },
    [getReviewResults, triggerToast]
  );

  const handleActionClick = async (
    actionKey: string,
    values: Record<string, any>
  ) => {
    if (actionKey === 'applyChanges') {
      const valuesPayload = buildReportPayload(values, dynamicData);
      setFilterValues({
        ...valuesPayload,
        reportTypeCode: 'RRR008',
      });

      if (
        valuesPayload.selectedTags.length === 0 &&
        valuesPayload.selectedRedactionTags.length === 0 &&
        valuesPayload.selectedCommentTypes.length === 0 &&
        valuesPayload.selectedProductions.length === 0 &&
        valuesPayload.selectedTagClassification.length === 0
      ) {
        return triggerToast(t('reviewResultsReport.warnings.emptySelection'), [
          ToastType.Warning,
        ]);
      }

      const saveResponse = await saveReviewResultsReportData(valuesPayload);
      setSaveApiCallResponse({
        notificationID: saveResponse.notificationID,
        reportID: saveResponse.reportID,
      });

      // update dataAsOf when user applies changes
      setDataAsOf(dayjs.utc().format('YYYY/MM/DD HH:mm:ss'));
    } else if (actionKey === 'reset') {
      setGridData(null);
      setFilterValues({
        reportTypeCode: 'RRR008',
      } as SaveReviewResultsReportDataPayload);
      setPageNumber(1);
      setSaveApiCallResponse({notificationID: 0, reportID: 0});
      setResetCounter((c) => c + 1);
    }
  };

  const saveReportConfigPayload = useMemo(() => {
    if (gridData?.reviewDetails?.dataTableRow && filterValues) {
      return {
        reportId: saveApiCallResponse.reportID,
        ...filterValues,
      };
    }

    return undefined;
  }, [gridData, filterValues]);

  const baseColumns = useMemo(() => {
    return createTableColumns(
      gridData?.reviewDetails?.dataTableHeader ?? [],
      gridData?.reviewDetails?.sourceType == 'FOLDERS' ? 'SourcePath' : ''
    );
  }, [gridData?.reviewDetails?.dataTableHeader]);

  const selectedColumnIndexes = useMemo(() => {
    if (!baseColumns?.length) return [];

    return selectedColumns
      .map((colId) => {
        const col = baseColumns.find((c) => c.field === colId);
        return col?.index;
      })
      .filter((v): v is number => typeof v === 'number');
  }, [selectedColumns, baseColumns]);

  const columns = useMemo(() => {
    if (!selectedColumnIndexes?.length) return baseColumns;

    return baseColumns.map((col) => ({
      ...col,
      isVisible: !selectedColumnIndexes.includes(col.index),
    }));
  }, [baseColumns, selectedColumnIndexes]);

  const handleColumnToggleAction = useCallback((columnId: string) => {
    setSelectedColumns((prev) => {
      let updated: string[];

      if (prev.includes(columnId)) {
        updated = prev.filter((id) => id !== columnId);
      } else {
        updated = [...prev, columnId];
      }

      localStorage.setItem('VisibleColumns', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const exportReport = useCallback(() => {
    const shouldUseLeftPanel = leftPanelResolvedReport && !didFiltersChange;
    const payload = {
      selectionCriteria: shouldUseLeftPanel
        ? leftPanelResolvedReport
        : {
            ...filterValues,
            selectedCustomColumns: selectedColumnIndexes,
            dataAsOf: dayjs
              .utc(dataAsOf, 'YYYY/MM/DD HH:mm:ss', true)
              .format('YYYY-MM-DDTHH:mm:ss'),
          },
      actionType: 'EXPORTREPORT',
    };
    exportReviewResultExcel(payload as any, {
      onSuccess: () => {
        triggerToast(t('reports.common.reportExportSuccess'), [
          ToastType.Success,
        ]);
      },
    });
  }, [
    didFiltersChange,
    leftPanelResolvedReport,
    filterValues,
    exportReviewResultExcel,
    dataAsOf,
    selectedColumnIndexes,
  ]);
  const docCountSummaryView = useMemo(() => {
    return (
      <>
        <DocCountSummaryView
          leftItems={[
            {
              label: t('reviewResultsReport.docCountSummary.totalDocs'),
              value:
                gridData?.reviewDetails?.totalDocuments === 0
                  ? 'N/A'
                  : (gridData?.reviewDetails?.totalDocuments ?? 'N/A'),
            },
            {
              label: t('reviewResultsReport.docCountSummary.totalTaggedDocs'),
              value:
                gridData?.reviewDetails?.totalTagedDocuments === 0
                  ? 'N/A'
                  : (gridData?.reviewDetails?.totalTagedDocuments ?? 'N/A'),
            },
            {
              label: t(
                'reviewResultsReport.docCountSummary.totalCommentedDocs'
              ),
              value:
                gridData?.reviewDetails?.totalCommentDocuments === 0
                  ? 'N/A'
                  : (gridData?.reviewDetails?.totalCommentDocuments ?? 'N/A'),
            },
          ]}
          rightItems={[
            {
              label: t(
                'reviewResultsReport.docCountSummary.totalTagClassificationDocs'
              ),
              value:
                gridData?.reviewDetails?.totalTagsClassificationDocuments === 0
                  ? 'N/A'
                  : (gridData?.reviewDetails
                      ?.totalTagsClassificationDocuments ?? 'N/A'),
            },
            {
              label: t('reviewResultsReport.docCountSummary.totalRedactedDocs'),
              value:
                gridData?.reviewDetails?.totalRedactionTagedDocuments === 0
                  ? 'N/A'
                  : (gridData?.reviewDetails?.totalRedactionTagedDocuments ??
                    'N/A'),
            },
            {
              label: t('reviewResultsReport.docCountSummary.totalProducedDocs'),
              value:
                gridData?.reviewDetails?.totalProductionDocuments === 0
                  ? 'N/A'
                  : (gridData?.reviewDetails?.totalProductionDocuments ??
                    'N/A'),
            },
          ]}
        />
      </>
    );
  }, [gridData, leftPanelResolvedReport]);

  const initialSelectedSources = useMemo(() => {
    if (isNil(leftPanelResolvedReport)) return {};
    return {
      [leftPanelResolvedReport.selectedSource]:
        leftPanelResolvedReport.selectedSourceID,
    };
  }, [leftPanelResolvedReport, resetCounter]);

  useEffect(() => {
    if (!isReviewResultsViewRoute) return;
    if (!reportIdFromUrl || reportIdFromUrl <= 0) return;
    if (loading) return;
    if (!mergedConfig) return;
    if (resetCounter > 0) return;
    if (saveApiCallResponse.notificationID > 0) return;
    if (saveApiCallResponse.reportID === reportIdFromUrl) return;
    setSaveApiCallResponse((prev) => ({
      ...prev,
      notificationID: 0,
      reportID: reportIdFromUrl,
    }));
  }, [
    isReviewResultsViewRoute,
    reportIdFromUrl,
    loading,
    mergedConfig,
    resetCounter,
    saveApiCallResponse.reportID,
  ]);

  useEffect(() => {
    if (!isReviewResultsViewRoute) return;
    if (!reportIdFromUrl || reportIdFromUrl <= 0) return;
    if (loading) return;
    if (!mergedConfig) return;
    if (resetCounter > 0) return;
    if (hasAutoFetchedViewReportRef.current) return;
    if (fromReportLanding) return;
    if (saveApiCallResponse.reportID !== reportIdFromUrl) return;
    hasAutoFetchedViewReportRef.current = true;
    fetchReportGridDataWithPagination(
      reportIdFromUrl,
      paginationSortConfigRef.current.sort,
      {
        pageNumber: paginationSortConfigRef.current.pageNumber,
        pageSize: paginationSortConfigRef.current.pageSize,
      }
    );
  }, [
    isReviewResultsViewRoute,
    reportIdFromUrl,
    loading,
    mergedConfig,
    resetCounter,
    saveApiCallResponse.reportID,
    fromReportLanding,
    fetchReportGridDataWithPagination,
  ]);
  const displayMessage = `Data as of ${dataAsOf} UTC`;
  const RIGHT_PANEL_HEADER_CONFIG: RightPanelConfig = {
    title: t('reviewResultsReport.aggregateSummary'),
    subTitle: displayMessage,
    showUploadIcon: true,
    showSaveIcon: true,
    showColumnsIcon: true,
    showActionButton: false,
    onUploadClick: exportReport,
  };
  return (
    <>
      {loading || !reportConfig ? (
        <div
          data-testid="loading-container"
          style={{display: 'flex', justifyContent: 'center', marginTop: '50px'}}
        >
          <ArrLoadingSpinner show={loading} overlay={true} isOpen={true} />
        </div>
      ) : (
        <StandardReports
          className="review-result-container"
          title={
            isReviewResultsViewRoute && fromReportLanding
              ? item?.label
              : reportConfig.title
          }
          showInfoIcon={reportConfig.showInfoIcon}
          infoContent={reportConfig?.infoContent}
          reportsConfig={mergedConfig}
          selectedOption={null}
          data={gridData}
          loading={loading}
          columns={columns}
          savedData={leftPanelResolvedReport}
          saveReportConfig={saveReportConfigPayload}
          mapRows={(data: any) =>
            data?.reviewDetails?.dataTableRow?.length == 0
              ? []
              : mapRowsWithHeaders(
                  data?.reviewDetails?.dataTableHeader ?? [],
                  data?.reviewDetails?.dataTableRow?.length
                    ? chunkRows(
                        data.reviewDetails.dataTableRow,
                        data.reviewDetails.dataTableHeader.length
                      )
                    : []
                )
          }
          sources={sources}
          rightPanelHeaderConfig={RIGHT_PANEL_HEADER_CONFIG}
          onAction={handleActionClick}
          onColumnToggleAction={handleColumnToggleAction}
          customHeaderContent={
            gridData?.reviewDetails?.dataTableRow?.length > 0 &&
            docCountSummaryView
          }
          initialSelectedSources={initialSelectedSources}
          gridConfig={{
            primaryKey: 'id',
            totalRecords: chunkRows(
              gridData?.reviewDetails?.dataTableRow,
              gridData?.reviewDetails?.dataTableHeader.length
            ).length,
            disableVirtualization: false,
            pageSizeOptions: REPORT_PAGE_SIZE_OPTIONS,
            itemsPerPage,
            currentPage: pageNumber,
            paginationMode: 'client',
            sortingMode: 'client',
            hideFooter:
              chunkRows(
                gridData?.reviewDetails?.dataTableRow,
                gridData?.reviewDetails?.dataTableHeader.length
              ).length <= PAGE_SIZE,
            getRowHeight: () => 'auto',
            setItemsPerPage: handleItemsPerPage,
            onPageChange: handlePageNumber,
          }}
        />
      )}

      {saveApiCallResponse?.notificationID > 0 &&
        saveApiCallResponse?.reportID > 0 && (
          <ArrWaitToCompleteModalWrapper
            key={`${saveApiCallResponse?.notificationID}-${saveApiCallResponse?.reportID}`}
            notificationId={saveApiCallResponse?.notificationID}
            reportId={saveApiCallResponse?.reportID}
            onComplete={(reportId: number) =>
              fetchReportGridDataWithPagination(
                reportId,
                paginationSortConfigRef.current.sort,
                {
                  pageNumber: paginationSortConfigRef.current.pageNumber,
                  pageSize: paginationSortConfigRef.current.pageSize,
                }
              )
            }
          />
        )}
      {showToast && (
        <ArrToastNotification
          message={toastMessage}
          status={toastStatus}
          onClose={() => setShowToast(false)}
        />
      )}
    </>
  );
};

export default ReviewResultsContainer;
